"use client";

import React, { useState, useCallback, useEffect } from "react";
import { ResponsiveModal } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Edit2, Plus } from "lucide-react";
import { useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import type {
  StaffLineItem,
  HardCostLineItem,
} from "@/lib/types/database/offerings";
import { OFFERING_ROLES } from "@/lib/types/database/offerings";
import { toStaffLineItem, toHardCostLineItem } from "./offering-line-items-utils";
import { StaffLineItemRow, HardCostLineItemRow } from "./OfferingLineItemRows";

export type LineItemsVariant = "staff" | "hard-cost";

interface OfferingLineItemsEditorProps {
  value: StaffLineItem[] | HardCostLineItem[] | null | undefined;
  fieldId: string;
  variant: LineItemsVariant;
  onSave: (newValue: StaffLineItem[] | HardCostLineItem[]) => Promise<void>;
  globalModalId?: string;
}

export function OfferingLineItemsEditor({
  value,
  fieldId,
  variant,
  onSave,
  globalModalId,
}: OfferingLineItemsEditorProps) {
  const { openModal, closeModal, isModalOpen } = useGlobalModalStack();
  const modalId = globalModalId || `line-items-editor-${fieldId}`;
  const isOpen = isModalOpen(modalId);

  const [items, setItems] = useState<StaffLineItem[] | HardCostLineItem[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const raw = Array.isArray(value) ? value : [];
      if (variant === "staff") {
        const normalized: StaffLineItem[] = raw
          .map(toStaffLineItem)
          .filter((x): x is StaffLineItem => x !== null);
        setItems(normalized);
      } else {
        const normalized: HardCostLineItem[] = raw
          .map(toHardCostLineItem)
          .filter((x): x is HardCostLineItem => x !== null);
        setItems(normalized);
      }
    }
  }, [isOpen, value, variant]);

  const handleAdd = useCallback(() => {
    if (variant === "staff") {
      setItems((prev) => [
        ...(prev as StaffLineItem[]),
        { role: "engineer", hours: 0, rate: undefined },
      ]);
    } else {
      setItems((prev) => [
        ...(prev as HardCostLineItem[]),
        { description: "other", amount: 0 },
      ]);
    }
  }, [variant]);

  const handleUpdate = useCallback(
    (index: number, updates: Partial<StaffLineItem> | Partial<HardCostLineItem>) => {
      setItems((prev) => {
        const next = [...prev];
        if (variant === "staff") {
          (next as StaffLineItem[])[index] = { ...(next[index] as StaffLineItem), ...(updates as Partial<StaffLineItem>) };
          return next as StaffLineItem[];
        } else {
          (next as HardCostLineItem[])[index] = { ...(next[index] as HardCostLineItem), ...(updates as Partial<HardCostLineItem>) };
          return next as HardCostLineItem[];
        }
      });
    },
    [variant]
  );

  const handleRemove = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index) as StaffLineItem[] | HardCostLineItem[]);
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setIsSaving(true);
      if (variant === "staff") {
        const toSave: StaffLineItem[] = (items as StaffLineItem[]).map((s) => {
          const roleDef = OFFERING_ROLES.find((r) => r.id === s.role);
          const effectiveRate = s.rate ?? roleDef?.defaultRate ?? 0;
          return { role: s.role, hours: s.hours, rate: effectiveRate };
        });
        await onSave(toSave);
      } else {
        const toSave: HardCostLineItem[] = (items as HardCostLineItem[]).map((h) => ({
          description: h.description,
          amount: h.amount,
        }));
        await onSave(toSave);
      }
      toast.success("Line items saved");
      closeModal(modalId);
    } catch (error) {
      console.error("Error saving line items:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save");
    } finally {
      setIsSaving(false);
    }
  }, [items, variant, onSave, modalId, closeModal]);

  const handleCancel = useCallback(() => {
    closeModal(modalId);
  }, [modalId, closeModal]);

  const displayLabel =
    variant === "staff" ? "Staff Line Items" : "Hard Cost Line Items";

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        title={`Edit ${displayLabel}`}
        onClick={() => openModal(modalId)}
      >
        <Edit2 className="h-4 w-4" />
      </Button>

      <ResponsiveModal
        globalModalId={modalId}
        title={`Edit ${displayLabel}`}
        description={
          variant === "staff"
            ? "Add staff roles, hours, and optional rate overrides"
            : "Add hard cost categories and amounts"
        }
        maxWidth="2xl"
        showCloseButton
      >
        <div className="space-y-4">
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {items.length === 0 ? (
              <div className="text-sm text-muted-foreground italic p-3 bg-muted rounded-md border border-dashed border-input">
                No line items yet
              </div>
            ) : (
              items.map((item, index) =>
                variant === "staff" ? (
                  <StaffLineItemRow
                    key={index}
                    item={item as StaffLineItem}
                    onUpdate={(u) => handleUpdate(index, u)}
                    onRemove={() => handleRemove(index)}
                  />
                ) : (
                  <HardCostLineItemRow
                    key={index}
                    item={item as HardCostLineItem}
                    onUpdate={(u) => handleUpdate(index, u)}
                    onRemove={() => handleRemove(index)}
                  />
                )
              )
            )}
          </div>

          <Button variant="outline" size="sm" onClick={handleAdd} className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add {variant === "staff" ? "Staff" : "Hard Cost"}
          </Button>

          <div className="flex gap-2 justify-end pt-2 border-t">
            <Button variant="outline" onClick={handleCancel} disabled={isSaving} size="sm">
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving} size="sm">
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
}
